import axios from 'axios';
import { API_URL, TIMEOUTS } from '@/utils/constants';


export class GPCService {
  constructor(baseURL, licenseToken) {
    this.licenseToken = licenseToken;
    this.sessionToken = this._getOrCreateSessionToken(licenseToken);

    this.api = axios.create({
      baseURL: baseURL || API_URL,
      timeout: TIMEOUTS.API_REQUEST,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    this.api.interceptors.request.use((config) => {
      if (this.licenseToken) {
        config.headers.Authorization = `Bearer ${this.licenseToken}`;
      }
      return config;
    });
  }

  _generateSessionToken() {
    return 'session_' + Array.from(crypto.getRandomValues(new Uint8Array(28)))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  _getStorageKey(licenseToken) {
    return `tiagpc_session_${licenseToken.substring(0, 16)}`;
  }

  _getOrCreateSessionToken(licenseToken) {
    const storageKey = this._getStorageKey(licenseToken);

    try {
      const storedData = localStorage.getItem(storageKey);

      if (storedData) {
        const { sessionToken, timestamp } = JSON.parse(storedData);
        const now = Date.now();
        const twentyFourHours = 24 * 60 * 60 * 1000;

        if (now - timestamp < twentyFourHours) {
          return sessionToken;
        } else {
          localStorage.removeItem(storageKey);
        }
      }
    } catch (error) {
      console.warn('[TIA GPC] Error leyendo sesión de localStorage:', error);
    }

    const newSessionToken = this._generateSessionToken();
    this._saveSessionToken(licenseToken, newSessionToken);

    return newSessionToken;
  }

  hasValidCachedSession() {
    const storageKey = this._getStorageKey(this.licenseToken);

    try {
      const storedData = localStorage.getItem(storageKey);

      if (storedData) {
        const { sessionToken, timestamp } = JSON.parse(storedData);
        const now = Date.now();
        const twentyFourHours = 24 * 60 * 60 * 1000;

        return (now - timestamp < twentyFourHours);
      }
    } catch (error) {
      console.warn('[TIA GPC] Error verificando sesión:', error);
    }

    return false;
  }

  _saveSessionToken(licenseToken, sessionToken) {
    const storageKey = this._getStorageKey(licenseToken);

    try {
      const data = {
        sessionToken,
        timestamp: Date.now()
      };
      localStorage.setItem(storageKey, JSON.stringify(data));
    } catch (error) {
      console.warn('[TIA GPC] Error guardando sesión en localStorage:', error);
    }
  }

  _clearSessionToken(licenseToken) {
    const storageKey = this._getStorageKey(licenseToken);
    try {
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.warn('[TIA GPC] Error limpiando sesión:', error);
    }
  }

  async sendMessage(message, turnstileToken, onChunk) {
    if (!this.licenseToken) {
      throw new Error('License token is required.');
    }

    const hasCachedSession = this.hasValidCachedSession();
    if (!turnstileToken && !hasCachedSession) {
      throw new Error('Turnstile token is required for anti-bot validation.');
    }

    const baseURL = this.api.defaults.baseURL;
    const url = `${baseURL}/ask`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.licenseToken}`
        },
        body: JSON.stringify({
          message,
          turnstile_token: turnstileToken || 'CACHED_SESSION',
          session_token: this.sessionToken
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(JSON.stringify({
          statusCode: response.status,
          errorCode: errorData.error_code || 'UNKNOWN_ERROR',
          message: errorData.message || 'Request failed',
          retryAfter: errorData.retry_after_seconds || null
        }));
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullResponse = '';
      let metadata = null;
      let summary = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.trim() || !line.startsWith('data: ')) continue;

          const data = line.replace('data: ', '').trim();
          if (data === '[DONE]') continue;

          try {
            const event = JSON.parse(data);

            if (event.type === 'content') {
              fullResponse += event.text;
              if (onChunk) onChunk(event.text);
            } else if (event.type === 'metadata') {
              metadata = event;
            } else if (event.type === 'summary') {
              summary = event;
            } else if (event.type === 'error') {
              throw new Error(JSON.stringify({
                statusCode: event.status_code || 500,
                errorCode: event.error_code,
                message: event.message,
                retryAfter: null
              }));
            }
          } catch (parseError) {
            if (parseError.message && parseError.message.startsWith('{')) {
              throw parseError;
            }
            console.error('Error parsing SSE event:', parseError);
          }
        }
      }

      if (!summary) {
        throw new Error('No summary received from server');
      }

      return {
        success: true,
        data: {
          message_id: summary.message_id,
          response: fullResponse,
          cost_usd: summary.cost_usd,
          credit_balance_usd: summary.credit_balance_usd,
          input_tokens: summary.input_tokens,
          output_tokens: summary.output_tokens,
          response_time_ms: summary.response_time_ms,
          model_used: summary.model_used,
          created_at: summary.created_at
        }
      };
    } catch (error) {
      if (error.message.startsWith('{')) {
        throw error;
      }
      throw new Error(JSON.stringify({
        statusCode: 500,
        errorCode: 'NETWORK_ERROR',
        message: error.message,
        retryAfter: null
      }));
    }
  }

  async releaseSession() {
    if (!this.licenseToken) {
      console.warn('No license token available to release session');
      return;
    }

    try {
      const response = await this.api.post('/release-session', {
        session_token: this.sessionToken
      });

      return response.data;
    } catch (error) {
      console.error('Failed to release session:', error.message);
    }
  }

  async getInitConfig() {
    if (!this.licenseToken) {
      throw new Error('License token is required.');
    }

    try {
      const response = await this.api.get('/init');
      return response.data;
    } catch (error) {
      this._handleError(error);
    }
  }

  async checkHealth() {
    try {
      const response = await this.api.get('/health');
      return response.data;
    } catch (error) {
      return {
        status: 'unhealthy',
        error: error.message
      };
    }
  }

  getSessionToken() {
    return this.sessionToken;
  }

  renewSessionToken() {
    const newSessionToken = this._generateSessionToken();
    this.sessionToken = newSessionToken;
    this._saveSessionToken(this.licenseToken, newSessionToken);
  }

  _handleError(error) {
    if (error.response) {
      const data = error.response.data;
      const status = error.response.status;

      const errorInfo = {
        statusCode: status,
        errorCode: data.error_code || 'UNKNOWN_ERROR',
        message: data.message || 'Request failed',
        retryAfter: data.retry_after_seconds || null
      };

      throw new Error(JSON.stringify(errorInfo));
    } else if (error.request) {
      throw new Error(
        JSON.stringify({
          statusCode: 503,
          errorCode: 'NETWORK_ERROR',
          message: 'No response from server',
          retryAfter: null
        })
      );
    } else {
      throw new Error(
        JSON.stringify({
          statusCode: 500,
          errorCode: 'INTERNAL_ERROR',
          message: error.message,
          retryAfter: null
        })
      );
    }
  }
}

let instance = null;

export const getGPCService = (baseURL, licenseToken) => {
  if (!instance || (licenseToken && instance.licenseToken !== licenseToken)) {
    instance = new GPCService(baseURL, licenseToken);
  }
  return instance;
};

export const resetGPCService = () => {
  if (instance) {
    instance.releaseSession().catch(() => { });
  }
  instance = null;
};